import { useMutation } from 'vue-query';
import { readonly, reactive, ref, unref, watch, computed } from '@vue/composition-api';
import { useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { usePrepaymentCheck } from '@services/service-type-bridge/atone-common';
import { LiteCapturePostResponseAuthorizationResultEnum } from '@netprotections/berlin-bff';
import { event } from 'vue-gtag';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, notNil, ValueTypeOf } from '@berlin-front/common/utils';
import { SERVICE_TYPE } from '@/services/ModalParameterContext';
import { broadcast } from '@/broadcast';
import { ROUTE_NAME } from '@/router/route-name';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

export const useLoading = () => ({
  useLoading: () => {
    const [isLegacy] = useIsLegacy();
    const router = useRouter();
    const SETTLEMENT_TIMEOUT = 20000;
    const { napoleon, js } = useContext(BerlinBffContext);
    const globals = useContext(ModalBridgeContext);
    const prePaymentCheck = usePrepaymentCheck();
    const { atoneLiteModalParameter, liteCapturePostResult, setLiteCapturePostResult, tokens } =
      useContext(ApiCacheContext);
    const requireJsPayment = ref(true);
    const completeProgress = ref(false);
    const nextPage = ref<
      undefined | { name: ValueTypeOf<typeof ROUTE_NAME>; query?: Record<string, string | string[]> }
    >();

    const AUTHORIZATION_RESULT = {
      OK: LiteCapturePostResponseAuthorizationResultEnum.NUMBER_1,
      NG: LiteCapturePostResponseAuthorizationResultEnum.NUMBER_2,
      PENDING: LiteCapturePostResponseAuthorizationResultEnum.NUMBER_3,
      ADDITIONAL_AUTH: LiteCapturePostResponseAuthorizationResultEnum.NUMBER_4,
    } as const;

    // TODO: prepaymentチェック。
    const {
      mutate: check,
      isSuccess: isSuccessPrepaymentCheck,
      data: prePaymentCheckResult,
    } = useMutation('prepaymentCheck', async () => prePaymentCheck());
    check();

    const {
      isSuccess: isSuccessJsPayment,
      isError: isErrorJsPayment,
      mutate: mutateJsPaymentCapture,
    } = useMutation(
      'atone.JsPaymentCapturePost',
      () =>
        js.atoneJavascriptControllerJavascriptPaymentCapture({
          id: getRefDeep(liteCapturePostResult, 'id'),
        }),
      {},
    );
    // APIの呼び出し(submit)
    const { isSuccess: isSuccessPayment, mutate } = useMutation(
      'lite.LiteCapturePost',
      () =>
        // @typescript-eslint/no-unsafe-call
        napoleon.liteControllerCapturePost(
          notNil(tokens.value?.npToken),
          notNil(atoneLiteModalParameter.value).pubKey,
          notNil(tokens.value.trustedTrackToken),
          {
            // TODO: anyブチとる
            amount: atoneLiteModalParameter.value.payment.amount,
            shopTransactionNo: atoneLiteModalParameter.value.payment.shopTransactionNo,
            userNo: atoneLiteModalParameter.value.payment.userNo,
            salesSettled: atoneLiteModalParameter.value.payment.salesSettled,
            transactionOptions: atoneLiteModalParameter.value.payment.transactionOptions as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            description: atoneLiteModalParameter.value.payment.description,
            // subtractPoint: 0,
            checksum: atoneLiteModalParameter.value.payment.checksum,
            sourceMedia: atoneLiteModalParameter.value.payment.sourceMedia,
            salesForm: atoneLiteModalParameter.value.payment.salesForm as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            customer: atoneLiteModalParameter.value.payment.customer as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            serviceSupplier: atoneLiteModalParameter.value.payment.serviceSupplier as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            destCustomers: atoneLiteModalParameter.value.payment.destCustomers,
            items: atoneLiteModalParameter.value.payment.items,
            isOldParameter: isLegacy.value,
            requestId: globals.requestId,
          },
          atoneLiteModalParameter.value.terminalId,
        ),
      {
        retry: false,
        onSuccess: ({ data }) => {
          setLiteCapturePostResult(data);
          const { authorizationResult, additionalAuthorizationResultNgReason } = data;
          if (authorizationResult === AUTHORIZATION_RESULT.OK) {
            // 決済成功。
            mutateJsPaymentCapture();
            // newrelic(決済成功)
            window.newrelic.addPageAction('payment_complete', {
              revenue: atoneLiteModalParameter.value.payment.amount,
              coupon: 0, // Liteはポイント利用がないので0で固定
              products: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
              shop_transaction_no: atoneLiteModalParameter.value.payment.shopTransactionNo,
            });

            event('purchase', {
              transaction_id: atoneLiteModalParameter.value.payment.shopTransactionNo,
              value: atoneLiteModalParameter.value.payment.amount,
              currency: 'JPY',
              coupon: 0,
              items: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
            });
          } else {
            // 決済失敗。
            requireJsPayment.value = false;
            if (authorizationResult === AUTHORIZATION_RESULT.PENDING) {
              // 着信認証へ
              nextPage.value = { name: ROUTE_NAME.ATONE_AUTH_CALL };
              return;
            }
            if (additionalAuthorizationResultNgReason === 1) {
              // 公的証明書確認画面を表示
              nextPage.value = { name: ROUTE_NAME.ATONE_FAIL_OFFICIAL_IDENTIFICATION };
            } else if (additionalAuthorizationResultNgReason === 2) {
              // アプリ認証画面を表示
              nextPage.value = { name: ROUTE_NAME.ATONE_FAIL_SPAPP_LOGIN };
            } else {
              // 与信失敗画面を表示
              nextPage.value = { name: ROUTE_NAME.ATONE_LITE_FAIL_AUTH_NG };
            }
            // newrelic(決済ng)
            window.newrelic.addPageAction('payment_ng', {
              revenue: atoneLiteModalParameter.value.payment.amount,
              coupon: 0, // Liteはポイント利用がないので0で固定
              products: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
              shop_transaction_no: atoneLiteModalParameter.value.payment.shopTransactionNo,
            });

            event('exception', {
              description: 'payment_ng',
              fatal: false,
              transaction_id: atoneLiteModalParameter.value.payment.shopTransactionNo,
              value: atoneLiteModalParameter.value.payment.amount,
              coupon: 0, // Liteはポイント利用がないので0で固定
              currency: 'JPY',
              items: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
            });
          }
        },
      },
    );

    watch([isSuccessPrepaymentCheck, prePaymentCheckResult] as const, ([isSuccess, result]) => {
      if (isSuccess) {
        switch (result) {
          case true: {
            mutate();
            break;
          }
          case false: {
            // TODO: lite用のカート不整合ルート作る。
            void router.replace({ name: ROUTE_NAME.ATONE_FAIL_CART });
            break;
          }
          default: {
            ((_v: undefined) => {})(result);
          }
        }
      }
    });

    const isCompleted = computed(() => {
      if (unref(requireJsPayment)) {
        return (unref(isSuccessPayment) && unref(isSuccessJsPayment)) || unref(isErrorJsPayment);
      }
      return unref(isSuccessPayment);
    });

    const isLoading = computed(() => !unref(isCompleted));

    watch([completeProgress, nextPage], () => {
      const complete = unref(completeProgress);
      const path = unref(nextPage);
      if (complete) {
        if (unref(liteCapturePostResult)?.authorizationResult === AUTHORIZATION_RESULT.OK) {
          broadcast.send({
            type: 'MODAL_CONTROL_SUCCEEDED',
            payload: {
              serviceType: SERVICE_TYPE.ATONE_LITE,
            },
          });
        }
        if (path) {
          void router.replace(path);
        }
      }
    });

    return readonly(
      reactive({
        title: '注文確定',
        onComplete: () => {
          completeProgress.value = true;
        },
        isLoading,
        isCompleted,
        duration: SETTLEMENT_TIMEOUT,
      }),
    );
  },
});
